import { useState, useRef, useCallback } from "react";
import { Button } from "../ui/Button";

interface VoiceRecorderProps {
  onRecorded: (blob: Blob, transcription?: string) => void;
  disabled?: boolean;
}

type RecordingState = "idle" | "recording" | "processing";

export function VoiceRecorder({ onRecorded, disabled }: VoiceRecorderProps) {
  const [state, setState] = useState<RecordingState>("idle");
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        setState("processing");
        onRecorded(blob);
        setState("idle");
        setDuration(0);
      };

      mediaRecorder.start();
      setState("recording");
      setDuration(0);

      timerRef.current = setInterval(() => {
        setDuration((d) => d + 1);
      }, 1000);
    } catch {
      setError("Microphone access denied. Please allow microphone access to record.");
      setState("idle");
    }
  }, [onRecorded]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!navigator.mediaDevices?.getUserMedia) {
    return (
      <p className="text-sm text-slate-500">
        Voice recording is not supported in this browser.
      </p>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {error && <p className="text-sm text-red-500">{error}</p>}

      {state === "recording" && (
        <div className="text-center">
          <p className="font-data text-2xl text-slate-900">{formatTime(duration)}</p>
          <p className="text-sm text-red-500">Recording...</p>
        </div>
      )}

      {state === "processing" && (
        <div className="text-center">
          <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          <p className="text-sm text-slate-500">Processing...</p>
        </div>
      )}

      <button
        type="button"
        onClick={state === "recording" ? stopRecording : startRecording}
        disabled={disabled || state === "processing"}
        className={`flex h-20 w-20 items-center justify-center rounded-full transition-all ${
          state === "recording"
            ? "animate-pulse bg-red-500 hover:bg-red-600"
            : "bg-blue-600 hover:bg-blue-700"
        } disabled:opacity-50`}
      >
        {state === "recording" ? (
          <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        ) : (
          <svg className="h-8 w-8 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
            <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
          </svg>
        )}
      </button>

      <p className="text-xs text-slate-400">
        {state === "idle" ? "Tap to record" : state === "recording" ? "Tap to stop" : ""}
      </p>
    </div>
  );
}
